import { inject, Injectable } from "@angular/core"
import { FireStoreService } from "@services/fire-store.service"
import { doc, onSnapshot } from "@angular/fire/firestore"
import { AngularFirestore } from "@angular/fire/compat/firestore"
import { ActivatedRoute, Params, Router } from "@angular/router"
import { MenuItem } from "primeng/api"
import { environment } from "../../environments/environment"
import { ThemingDTO } from "@services/program.service"
import { LogsService } from "@services/logs.service"
import { AuthService } from "@services/auth.service"

export interface SectionPubDTO {
  id: string
  title: string
  subSections: Array<SubSectionPubDTO>
}

export interface ProgramInfo {
  id: string
  date_start: Date
  date_end: Date
}

export interface SubSectionPubDTO {
  id: string

  title: string
  content: string
  backgroundImg?: string

  hideTitleSection: boolean
}

export interface ProgramPubDTO {
  id?: string
  version: string

  disabledMessage: string
  date_published: Date
  accessible: boolean
  name: string
  town: string
  date: string
  intro: SubSectionPubDTO

  theming: ThemingDTO

  sections: Array<SectionPubDTO> // private => connected for accessing
}

@Injectable({
  providedIn: "root",
})
export class ProgramPublishedService extends FireStoreService<ProgramPubDTO> {
  angularFirestore = inject(AngularFirestore)
  router = inject(Router)
  activatedRoute = inject(ActivatedRoute)
  logsService = inject(LogsService)
  authService = inject(AuthService)

  public summaryItems: MenuItem[] = []
  public currentProgram: ProgramPubDTO | undefined
  public currentSubSectionIndex = 0
  public currentSubSections: Array<SubSectionPubDTO> = []
  private programsCache = new Map<string, Array<ProgramPubDTO>>()

  constructor() {
    super()
    this.init({
      collectionKey: `program-published`,
      fullTextFields: [],
    })
    this._init()
  }

  async getMyPrograms(uid: string): Promise<Array<ProgramPubDTO> | undefined> {
    return this.programsCache.get(uid)
  }

  async setMyPrograms(uid: string, programs: Array<ProgramPubDTO>) {
    this.programsCache.set(uid, programs)
  }

  private async _init() {
    const prog = await this.getProgramOnAir()
    const currentProgId = prog?.id || ""
    if (currentProgId) {
      console.log("listening to program-published", currentProgId)
      let first2 = true
      onSnapshot(doc(this.angularFirestore.firestore, "program-published", currentProgId), (snapshot) => {
        if (first2 || !this.currentProgram) {
          // ignoring the first update (during init)
          first2 = false
          return
        }
        const route = this.router.url || ""

        if (route.includes("program") && !route.includes("admin")) {
          const prog = snapshot.data() as any
          console.log("Program updated!", prog)
          if (prog && prog.accessible === false && this.currentProgram?.accessible) {
            this.currentProgram.accessible = false
            this.currentProgram.disabledMessage = prog.disabledMessage
            this.notifService.displayInfo("Le programme est devenu inaccessible")
          } else if (!this.currentProgram.accessible && prog.accessible === true) {
            this.currentProgram.accessible = true
            this.notifService.displayInfo("Le programme est de nouveau accessible")
          } else if (prog.accessible) {
            this.notifService.displaySuccess("Le programme vient d'être mis à jour à l'instant.")
            setTimeout(() => {
              this.notifService.displayInfo("Rechargez pour actualiser.")
            }, 2000)
          }
        }
      })
    }
  }
  updateTheming(program: ProgramPubDTO) {
    const route = this.router.url || ""
    if (route.includes("admin")) return
    const theming = program.theming
    if (!theming) return

    // Hack
    const excludeFullPage = ".main-container:not(.full-page) "
    const hackCssFACJ = `
    ${excludeFullPage}.title {background: linear-gradient(180deg, rgba(243,241,228,1) 0%, rgba(243,241,228,0.8) 100%) !important; top: 74px !important;}
    .separator2 { background-image: none;}
    .header {height: 27px !important;width: 145px !important;}
    .footer {background-repeat: no-repeat; background-position: center top !important;margin-top:0 !important;}
    app-home .header {height: 70px !important;width: auto !important;background-repeat: no-repeat;
    background-position: center; margin-bottom: 30px;}
    .overlaypanelmenu2 .selected { background-color: rgba(0, 0, 0, 0.1) !important;}
    .btn-send:hover {background-color: rgba(0,0,0,0.2)  !important;}
    `

    //TODO gradient subsection-title
    const bg = [
      "body",
      `${excludeFullPage}.header-fixed`,
      ".pi-arrow-right",
      ".pi-arrow-left",
      `${excludeFullPage}.subsection-title`,
    ]
    const text = [
      ".title",
      `.pi.pi-bars`,
      ".subsection",
      ".pi-arrow-right",
      ".pi-arrow-left",
      ".program-buttons-bottom",
      ".subsection-title",
      "app-home *",
      ".overlaypanelmenu2 *",
      "p-confirmdialog *",
      "app-info-detail",
      ".inner-html-quill",
    ]
    const bgMenu = [".p-dialog-header", ".p-dialog-content", ".p-dialog-footer"]
    this.addStyle(
      bgMenu.map((selector) => `${selector} { background-color: #${theming.menuBgColor} !important; }`).join("") +
        bg.map((selector) => `${selector} { background-color: #${theming.bgColor} !important; }`).join("") +
        `.btn-send {border-color: #${theming.textColor}  !important;}` +
        text.map((selector) => `${selector} { color: #${theming.textColor} !important; }`).join("") +
        (theming.logoSrc ? `.header { background-image: url("${theming.logoSrc}") !important;}` : "") +
        (theming.footerSrc ? `.footer { background-image: url("${theming.footerSrc}") !important;}` : "") +
        (program.name?.toLowerCase()?.includes("complètement") ? hackCssFACJ : "")
    )
  }

  addStyle(styles: string, id: string = "dynamic-custom-style") {
    let css = document.getElementById(id) as HTMLStyleElement

    if (!css) {
      css = document.createElement("style")
      css.id = id
      css.type = "text/css"
      document.getElementsByTagName("head")[0].appendChild(css)
    }

    css.textContent = styles
  }

  async initProgramWithSection(programId: string, subId = ""): Promise<any> {
    this.currentProgram = undefined
    const program = await this.get(programId)
    const sections = await this.getSections(programId)
    program.sections = sections || []

    this.updateTheming(program)
    this.currentProgram = program
    this.currentSubSections = []
    this.currentProgram?.sections.forEach((section) => {
      section?.subSections.forEach((sub) => {
        this.currentSubSections.push(sub)
      })
    })
    this.currentSubSectionIndex = 0
    if (subId) {
      const index = this.currentSubSections.findIndex((s) => s.id === subId)
      if (index >= 0) this.currentSubSectionIndex = index
    }
    this.buildSummaryItems()
    await this.updateQueryParams()
  }

  async updateQueryParams() {
    const index = this.currentSubSectionIndex
    if (index >= 0 && index < this.currentSubSections.length) {
      const sub = this.currentSubSections[index]
      const subId = sub?.id
      const queryParams: Params = { id: subId }
      await this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams,
        replaceUrl: true,
      })
      if (environment.production)
        gtag("event", "page_view", {
          page_title: sub?.title || sub?.id || "Unknown",
          page_location: window.location.pathname + window.location.search,
        })

      this.logsService.sendLog(
        this.authService.getCurrentUser()?.uid || "",
        this.currentProgram?.id || "",
        `visit[#]${sub?.id}[#]${sub?.title}`
      )
    }
    this.updateSummarySelected()
  }

  updateSummarySelected() {
    const index = this.currentSubSectionIndex
    if (index >= 0 && index < this.currentSubSections.length) {
      const sub = this.currentSubSections[index]
      this.summaryItems.forEach((s) => (s.styleClass = s.styleClass?.replace(/ selected/g, "")))
      const arr = this.summaryItems.filter((s) => s.id === sub.id && s.label)
      const sum = arr.length > 0 ? arr[arr.length - 1] : undefined
      if (sum) {
        sum.styleClass += " selected"
      }
    }
  }

  buildSummaryItems() {
    let index = 0
    this.summaryItems = [
      {
        label: "", //"SOMMAIRE",
        styleClass: "menu-section-summary",
        command: () => {},
      },
    ]
    this.currentProgram?.sections.forEach((s) => {
      const theIndex = index
      this.summaryItems.push({
        id: s?.subSections[0]?.id || s.id,
        label: s.title,
        styleClass: "menu-section",
        command: () => {
          if (this.currentSubSectionIndex !== theIndex) {
            this.currentSubSectionIndex = theIndex
            this.updateQueryParams()
            this.scrollTop()
          }
        },
      })

      s.subSections.forEach((sub) => {
        const theIndex2 = index
        if (s.subSections.length > 1 && sub.title)
          this.summaryItems.push({
            id: sub.id,
            label: sub.title,
            styleClass: "",
            command: () => {
              if (this.currentSubSectionIndex !== theIndex2) {
                this.currentSubSectionIndex = theIndex2
                this.updateQueryParams()
                this.scrollTop()
              }
            },
          })
        index++
      })

      this.summaryItems.push({
        label: "",
        styleClass: "separator",
        command: () => {},
      })
      // this.summaryItems.push({ separator: true })
    })
  }

  async saveCurrentProgram(id: string, date_start: Date | undefined, date_end: Date | undefined): Promise<any> {
    return this.firestore
      .collection(`utils`)
      .doc("currentProgram")
      .set({ id, date_start, date_end })
      .catch((err) => this.handleError(err, null))
  }

  async getProgramOnAir(): Promise<ProgramPubDTO | undefined> {
    const info = await this.getCurrentProgramInfo()
    if (info?.id && info?.date_start && info?.date_end) {
      if (this.isProgramInfoOnAir(info)) return this.get(info.id)
    }
    return undefined
  }

  async getProgramIdFromAlias(alias: string): Promise<string> {
    return this.firestore
      .collection(`alias`)
      .doc(alias)
      .get()
      .toPromise()
      .then((res: any): string => {
        const data = res.data && res.data()
        return data?.id || ""
      })
      .catch((err) => this.handleError(err, ""))
  }

  isProgramInfoOnAir(info: ProgramInfo): boolean {
    const now = Date.now()
    return info?.date_start.getTime() < now && now < info?.date_end.getTime()
  }

  public getCurrentProgramInfo(): Promise<ProgramInfo> {
    return this.firestore
      .collection(`utils`)
      .doc("currentProgram")
      .get()
      .toPromise()
      .then((res: any) => {
        const dto = res.data()
        this._udpatingFirestoreData(dto)
        return dto
      })
      .catch((err) => this.handleError(err, null))
  }

  getCurrentSection(): SectionPubDTO | undefined {
    const currentSubsection = this.getCurrentSubSection()
    if (currentSubsection && this.currentProgram) {
      return this.currentProgram.sections.find((sec) => sec.subSections?.find((sub) => sub.id === currentSubsection.id))
    }
    return
  }

  getCurrentSubSection(): SubSectionPubDTO | undefined {
    if (this.currentSubSections && this.currentSubSectionIndex < this.currentSubSections.length) {
      return this.currentSubSections[this.currentSubSectionIndex]
    }
    return
  }

  prev() {
    if (this.currentSubSectionIndex > 0) {
      this.currentSubSectionIndex--
      this.updateQueryParams()
    }
  }

  next() {
    if (this.currentSubSectionIndex < this.currentSubSections.length - 1) {
      this.currentSubSectionIndex++
      this.updateQueryParams()
    }
  }

  getSections(programId: string): Promise<Array<SectionPubDTO>> {
    return this.firestore
      .collection(`${this.config.collectionKey}/${programId}/private`)
      .doc("sections")
      .get()
      .toPromise()
      .then((raw: any) => {
        return raw.data()?.sections
      })
      .catch((err) => this.handleError(err, null))
  }

  addSections(programId: string, sections: Array<SectionPubDTO>): Promise<any> {
    return this.firestore
      .collection(`${this.config.collectionKey}/${programId}/private`)
      .doc("sections")
      .set({ sections })
      .catch((err) => this.handleError(err, null))
  }

  scrollTop() {
    window.scroll({
      top: 0,
      left: 0,
    })
  }

  titleIsDisplayed(): boolean {
    const currentSubsection = this.getCurrentSubSection()
    const currentSection = this.getCurrentSection()
    return currentSection?.subSections[0]?.id === currentSubsection?.id
  }
}
